import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { PossibleDuplicate } from '@/api/people';
import { type } from '@/theme/typography';

interface Props {
  /** The matches: §6.4's pre-check, or the 409 of `POST /people/` or of `PATCH /people/{id}/`. */
  duplicates: PossibleDuplicate[];
  /**
   * The one sentence above the list, in the words of the screen that asks — *"This may be someone
   * you already have"* on §6.4's create, *"These changes match someone you already have"* on §6.5's
   * edit. It is a prop rather than a constant because the two screens are asking different
   * questions about the same list.
   */
  heading: string;
  /**
   * §6.4's **per-match primary** — *"Add a new consideration to the existing card"*, which is ADR
   * 0002's answer to a **create**. §6.5 passes nothing: nothing on the person card opens or moves a
   * run and `PersonUpdate` carries no `role_id`, so a per-match action there would be one that
   * cannot work.
   */
  actionFor?: (duplicate: PossibleDuplicate) => ReactNode;
  /**
   * The action that is about the **form** rather than about a particular match, rendered after every
   * per-match one — in the DOM as well as in style, so a keyboard user reaches the action that keeps
   * one card first (§6.4's rule, and §6.5 has only this slot filled).
   */
  children?: ReactNode;
}

/**
 * The three keys ADR 0014 matches on, **hand-listed because the document does not carry them as a
 * vocabulary**: `matched_on` is typed `string[]`, so there is nothing to derive from the schema.
 *
 * A key this list does not name is therefore dropped rather than printed: `full_name` shown to a
 * recruiter is a column name, not a reason, and the sentence below has a form for "matched, and the
 * reason has no words here".
 */
const NAMED_MATCH_KEYS: readonly string[] = ['full_name', 'email', 'phone'];

/** `2026-07-01T09:00:00Z` → the reader's own medium date. No English literal, as `updatedAt.ts`. */
function openedOn(iso: string, locale: string): string {
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return '';

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(instant);
}

/**
 * **How a possible duplicate is presented — the half §6.4 and §6.5 genuinely share** (TASK-112).
 *
 * The sentence naming the person and their runs with the date each opened, **why** the matcher thinks
 * it is them (`matched_on`), and a link to that card. That last part is the whole of it: *"this may be
 * a duplicate"* with no reason is not something a recruiter can act on, because only they know whether
 * two Ada Lovelaces are one person, and the card is the shortest way for them to find out.
 *
 * ⚠️ **The actions are deliberately not here, and that is the judgement TASK-112 was asked to make.**
 * §6.4's primary — *"Add a new consideration to the existing card"* — is ADR 0002's answer to a
 * **create** and is meaningless on an edit (nothing on §6.5 opens or moves a run), and its secondary
 * is labelled *"Create a separate person"*, which is not what saving an edit does. So each screen
 * passes its own: §6.4 a per-match primary and one secondary, §6.5 a single secondary and no primary.
 * Generalising the actions as well would have given one of the two screens a button that cannot work.
 *
 * `role="status"` because on §6.4 this arrives **while the recruiter is typing** rather than in answer
 * to a press — the pre-check settles a moment after the pause. On §6.5 it does answer a press, and the
 * live region still earns its place there: the dialog's own *Save changes* is withdrawn in the same
 * moment (`EditPersonDialog`), so a reader who is not looking at this block is told why the button
 * they were aiming at has gone. It is not a toast either — §4 keeps a toast for what needs no answer,
 * and this carries the actions.
 */
export function DuplicateMatches({ duplicates, heading, actionFor, children }: Props) {
  const { t, i18n } = useTranslation('candidates');

  /** Which keys matched, in words. Empty when the API named none this build has a word for. */
  const matchedOn = (duplicate: PossibleDuplicate) =>
    duplicate.matched_on
      .filter((key) => NAMED_MATCH_KEYS.includes(key))
      .map((key) => t(`duplicate.keys.${key}`));

  /** The sentence: who, and what they were already considered for. */
  const existsSentence = (duplicate: PossibleDuplicate) => {
    if (duplicate.considerations.length === 0) {
      return t('duplicate.existsWithoutRuns', { name: duplicate.full_name });
    }

    return t('duplicate.exists', {
      name: duplicate.full_name,
      runs: duplicate.considerations
        .map((consideration) =>
          t('duplicate.run', {
            vacancy: consideration.role_title,
            date: openedOn(consideration.opened_at, i18n.language),
          }),
        )
        .join('; '),
    });
  };

  return (
    <div
      role="status"
      className="flex flex-col gap-4 rounded border border-rule-control bg-paper-sunken p-4"
    >
      <p className="text-sm font-medium text-ink-900">{heading}</p>

      <ul className="flex flex-col gap-4">
        {duplicates.map((duplicate) => {
          const keys = matchedOn(duplicate);

          return (
            <li key={duplicate.id} className="flex flex-col items-start gap-2">
              <p className="text-sm text-ink-900">{existsSentence(duplicate)}</p>
              <p className={type.metadata}>
                {keys.length > 0
                  ? t('duplicate.matchedOn', { keys: keys.join(', ') })
                  : t('duplicate.matchedOnUnnamed')}
              </p>

              {/*
               * The card itself, before deciding anything — the shortest way to answer *"is this the
               * same person"*, which is the only question the recruiter can answer and the client
               * cannot.
               */}
              <Link
                to={`/candidates/${duplicate.id}`}
                className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {t('duplicate.openCard', { name: duplicate.full_name })}
              </Link>

              {actionFor?.(duplicate)}
            </li>
          );
        })}
      </ul>

      {children}
    </div>
  );
}
